import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const STORAGE_KEY_STORE_ID = 'kiosk_store_id';
const STORAGE_KEY_TERMINAL_ID = 'kiosk_terminal_id';
const STORAGE_KEY_DEVICE_TOKEN = 'kiosk_device_token';
const STORAGE_KEY_DUAL = 'kiosk_dual';

// The kiosk shell's only way to hand this page its own storeId/terminalId is
// the URL it loads (no bridge reaches the ordering site outside Electron).
// This captures those once on whichever page first sees them and persists
// them to sessionStorage, so later in-app navigation can't silently drop
// them the way a URL-only approach can.
//
// deviceToken rides the same mechanism, proving to the backend this session
// really is the specific enrolled device it claims (not just a storeId
// string). It's a bearer credential, not an identifier, so it's stripped
// from the visible URL once captured to keep it out of history/referrers.
export function useKioskIdentity() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlStoreId = searchParams.get('storeId');
  const urlTerminalId = searchParams.get('terminalId');
  const urlDeviceToken = searchParams.get('deviceToken');
  const urlDual = searchParams.get('dual');

  useEffect(() => {
    if (urlStoreId) sessionStorage.setItem(STORAGE_KEY_STORE_ID, urlStoreId);
    if (urlTerminalId) sessionStorage.setItem(STORAGE_KEY_TERMINAL_ID, urlTerminalId);
    if (urlDual) sessionStorage.setItem(STORAGE_KEY_DUAL, urlDual);
    if (urlDeviceToken) {
      sessionStorage.setItem(STORAGE_KEY_DEVICE_TOKEN, urlDeviceToken);
      const next = new URLSearchParams(searchParams);
      next.delete('deviceToken');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlStoreId, urlTerminalId, urlDeviceToken, urlDual]);

  const storeId = urlStoreId || sessionStorage.getItem(STORAGE_KEY_STORE_ID) || null;
  const terminalId = urlTerminalId || sessionStorage.getItem(STORAGE_KEY_TERMINAL_ID) || null;
  const deviceToken = urlDeviceToken || sessionStorage.getItem(STORAGE_KEY_DEVICE_TOKEN) || null;

  // Two screens: the operator is serving the customer, so a merchant copy is printed too.
  const dual = (urlDual || sessionStorage.getItem(STORAGE_KEY_DUAL)) === '1';

  return { storeId, terminalId, deviceToken, dual };
}
